import type { DashboardFilter } from '../../../types/insights'

/**
 * Pure dashboard filter state — no fetch, no router, no DOM. Vitest-friendly
 * by construction (design doc §2.2/§2.3). Two independent sources compose:
 *   - `filters`  — explicit facet/refinement controls (fiscal year, program, etc.)
 *   - `crossFilters` — added by clicking a chart element (AC10); same axis
 *     shape as `filters`, kept separate only so the FilterChipBar can render
 *     provenance (removed the same way, AND-composed the same way).
 * Every active entry across both maps is ANDed together (AC11); nothing here
 * ever represents an OR.
 */

export interface DashboardFilterState {
  filters: Partial<Record<keyof DashboardFilter, string>>
  crossFilters: Partial<Record<keyof DashboardFilter, string>>
  sliders: Record<string, [number, number]>
  territory?: string | undefined
  asOf?: { from?: string | undefined; to?: string | undefined } | undefined
  /** The widget key that most recently added a cross-filter — lets the dashboard skip re-fetching its own origin widget (AC10). */
  crossFilterOrigin?: string | undefined
}

export type DashboardFilterAction =
  | { type: 'SET_FILTER'; axis: keyof DashboardFilter; value: string | undefined }
  | { type: 'ADD_CROSSFILTER'; axis: keyof DashboardFilter; value: string; sourceWidget: string }
  | { type: 'REMOVE_CROSSFILTER'; axis: keyof DashboardFilter }
  | { type: 'SET_SLIDER'; axis: string; range: [number, number] }
  | { type: 'REMOVE_SLIDER'; axis: string }
  | { type: 'SET_TERRITORY'; territory: string | undefined }
  | { type: 'SET_AS_OF'; from?: string | undefined; to?: string | undefined }
  | { type: 'CLEAR_ALL' }

export const initialDashboardFilterState: DashboardFilterState = {
  filters: {},
  crossFilters: {},
  sliders: {},
}

export function dashboardFilterReducer(
  state: DashboardFilterState,
  action: DashboardFilterAction,
): DashboardFilterState {
  switch (action.type) {
    case 'SET_FILTER': {
      const filters = { ...state.filters }
      if (action.value === undefined || action.value === '') delete filters[action.axis]
      else filters[action.axis] = action.value
      return { ...state, filters }
    }
    case 'ADD_CROSSFILTER': {
      return {
        ...state,
        crossFilters: { ...state.crossFilters, [action.axis]: action.value },
        crossFilterOrigin: action.sourceWidget,
      }
    }
    case 'REMOVE_CROSSFILTER': {
      const crossFilters = { ...state.crossFilters }
      delete crossFilters[action.axis]
      return { ...state, crossFilters, crossFilterOrigin: undefined }
    }
    case 'SET_SLIDER':
      return { ...state, sliders: { ...state.sliders, [action.axis]: action.range } }
    case 'REMOVE_SLIDER': {
      const sliders = { ...state.sliders }
      delete sliders[action.axis]
      return { ...state, sliders }
    }
    case 'SET_TERRITORY':
      return { ...state, territory: action.territory }
    case 'SET_AS_OF':
      return { ...state, asOf: { from: action.from, to: action.to } }
    case 'CLEAR_ALL':
      return initialDashboardFilterState
    default:
      return state
  }
}

/**
 * Derives the effective `DashboardFilter` sent to every widget endpoint —
 * `filters` and `crossFilters` are ANDed; a cross-filter on the same axis as
 * an explicit filter takes precedence (it is the more specific, most recent
 * user action) but both are always additive, never OR'd (AC11).
 */
export function toDashboardFilter(state: DashboardFilterState): DashboardFilter {
  const merged: Partial<Record<keyof DashboardFilter, string>> = { ...state.filters, ...state.crossFilters }
  const filter: DashboardFilter = { ...merged }
  if (state.territory) filter.jurisdiction = state.territory
  if (state.asOf?.from) filter.asOfFrom = state.asOf.from
  if (state.asOf?.to) filter.asOfTo = state.asOf.to
  return filter
}

export interface DashboardFilterChip {
  id: string
  axis: string
  value: string
  kind: 'filter' | 'cross-filter' | 'territory' | 'slider'
}

/** Every currently active, removable chip — used by both FilterChipBar and tests. */
export function activeChips(state: DashboardFilterState): DashboardFilterChip[] {
  const chips: DashboardFilterChip[] = []
  for (const [axis, value] of Object.entries(state.filters)) {
    if (value) chips.push({ id: `f.${axis}`, axis, value, kind: 'filter' })
  }
  for (const [axis, value] of Object.entries(state.crossFilters)) {
    if (value) chips.push({ id: `x.${axis}`, axis, value, kind: 'cross-filter' })
  }
  for (const [axis, range] of Object.entries(state.sliders)) {
    chips.push({ id: `s.${axis}`, axis, value: `${range[0]}–${range[1]}`, kind: 'slider' })
  }
  if (state.territory) chips.push({ id: 't', axis: 'territory', value: state.territory, kind: 'territory' })
  return chips
}
